import type { FlowIdentifier } from '@okta/okta-auth-js/types/lib/idx/types/FlowIdentifier'
import OktaSignIn, { EventContext, EventErrorContext } from '@okta/okta-signin-widget'

import logger from '@ua-okta-ui/lib/logger'
import { ApplicationConfig, BrandConfig, Locale, TermsAndConditions } from '@ua-okta-ui/types'
import { Brands, ContextViews, Flows, Hints } from '@ua-okta-ui/types/enum'
import {
	createTermsAndConditionsFooter,
	findFooter,
	getStorefrontUrl,
	getTermsAndConditions,
	getTermsAndConditionsContext,
	getTermsAndConditionsForContext,
	loadTranslationFromStorage,
	mutateButton,
	mutateElement,
	parseContext,
} from '@ua-okta-ui/lib/okta'

export class UaOktaWidget {
	#application: ApplicationConfig
	#brand: BrandConfig
	#element: string
	#flow?: FlowIdentifier
	#hint?: Hints
	#locale: Locale
	#previousContext: ContextViews | undefined
	#termsAndConditions: TermsAndConditions
	#translations: Record<string, string>
	#widget: OktaSignIn
	#loginContainerDiv: Element | null = null

	constructor({
		application,
		brand,
		element,
		locale,
		widget,
	}: {
		application: ApplicationConfig
		brand: BrandConfig
		element: string
		locale: Locale
		widget: OktaSignIn
	}) {
		this.#application = application
		this.#brand = brand
		this.#element = element
		this.#flow = globalThis.uaOkta.flow
		this.#hint = globalThis.uaOkta.hint
		this.#locale = locale
		// TODO: use a fallback?
		this.#termsAndConditions = getTermsAndConditions({ brand, locale })
		this.#translations = loadTranslationFromStorage({ locale, logger: this.#logError })
		this.#widget = widget
		this.#loginContainerDiv = document.querySelector(this.#element)

		this.#widget.after('identify-recovery', () => {
			this.#logInfo('reset password rendered')
		})

		this.#logInfo('init widget', { flow: uaOkta.flow })
	}

	#mutateElements({ context }: { context: ContextViews }): void {
		if (this.#loginContainerDiv && this.#hint === Hints.UPDATE_PASSWORD && this.#flow === Flows.RESET_PASSWORD) {
			mutateElement({
				element: this.#loginContainerDiv,
				selector: 'div > button[data-se="cancel"]',
				condition: () => context === ContextViews.RESET_PASSWORD_2,
				mutate: () =>
					mutateButton({
						onclick: (e) => {
							e.preventDefault()
							window.location.href = 'javascript:history.back()'
						},
						selector: (element) => `form>div.MuiBox-root>div.MuiBox-root>${element}[data-se="cancel"]`,
						text: this.#translations['oform.cancel'] ?? 'Cancel',
					}),
			})
		}

		if (this.#loginContainerDiv && this.#brand.name === Brands.WEB_KR && context === ContextViews.LOGIN) {
			const storefrontUrl = getStorefrontUrl(this.#brand)

			if (storefrontUrl) {
				mutateElement({
					element: this.#loginContainerDiv,
					selector: 'div > button[data-se="enroll"]',
					mutate: () =>
						mutateButton({
							onclick: (e) => {
								e.preventDefault()
								window.location.href = `${storefrontUrl}/ko-kr/?showRegisterModal=true`
							},
							selector: (element) =>
								`form>div.MuiBox-root>div.MuiBox-root>div.MuiBox-root>${element}[data-se="enroll"]`,
							text: this.#translations['registration.signup.text'],
						}),
				})
			}
		}
	}

	addTermsAndConditions({ context }: { context: ContextViews }): void {
		const termsContext = getTermsAndConditionsContext(context)

		if (!termsContext) {
			this.#logError('Terms: Unsupported Context', { context })
			return
		}

		const termsAndConditions = getTermsAndConditionsForContext({
			context: termsContext,
			termsAndConditions: this.#termsAndConditions,
		})

		if (!termsAndConditions) {
			this.#logError('Terms: Could not find terms and conditions', { context, termsContext })
			return
		}

		const footer = findFooter()

		if (!footer) {
			this.#logError('Terms: Could not locate footer', { context, termsAndConditions, termsContext })
			return
		}

		const tacDiv = createTermsAndConditionsFooter(termsAndConditions)
		footer.appendChild(tacDiv)
	}

	#handleAfterRender(eventContext: EventContext) {
		const context = parseContext(eventContext.formName)

		if (!context || !this.#loginContainerDiv) {
			this.#logError('afterRender: Error initializing context, missing required data', {
				context,
				loginContainerDiv: !!this.#loginContainerDiv,
			})

			return
		}

		if (context === ContextViews.SUCCESS_REDIRECT) {
			this.#logInfo(context, { eventContext })
			this.#widget.hide()
			return
		} else if (context === ContextViews.FAILURE_REDIRECT) {
			this.#logError(context, { eventContext })
		}

		this.#loginContainerDiv.setAttribute('data-form-context', context)
		this.#loginContainerDiv.classList.add(context)

		try {
			this.#automateActions({ context })
			this.#hideFormElements({ context })
			this.#removeFooter()
			this.addTermsAndConditions({ context })
			this.#mutateElements({ context })
			if (this.#brand.name === Brands.WEB_KR) {
				this.#removeNaverSSO()
				this.#addNaverSSO({ context })
			}
			this.#previousContext = context
		} catch (e) {
			const error = e as Error
			this.#logError('afterRender: Error initializing form context', { error: error.message })
		}
	}

	#automateActions({ context }: { context: ContextViews }) {
		if (
			context === ContextViews.SELECT_AUTHENTICATOR &&
			this.#previousContext === ContextViews.REGISTER &&
			!!document.querySelector("h3[data-se='select-authenticator-enroll_Heading_Optional_3']")
		) {
			document.querySelector<HTMLButtonElement>("button[data-se='save']")?.click()
		} else if (
			context === ContextViews.SELECT_AUTHENTICATOR &&
			!!document.querySelector("h3[data-se='select-authenticator-enroll_Heading_Required_now_3']")
		) {
			document.querySelector<HTMLButtonElement>("button[data-se='authenticator-button']")?.click()
		}
	}

	#handleReady(eventContext: EventContext) {
		const context = parseContext(eventContext.formName)
		this.#triggerFlow({ context })
	}

	#addNaverSSO({ context }: { context: ContextViews }): void {
		const storefrontUrl = getStorefrontUrl(this.#brand)
		if (!storefrontUrl || [ContextViews.LOGIN, ContextViews.REGISTER].includes(context) === false) {
			return
		}
		const naverSSODiv = document.createElement('div')
		naverSSODiv.classList.add('naver-sso')
		const naverSSOButton = document.createElement('a')
		naverSSOButton.classList.add('naver-sso-button')
		const mode = context === ContextViews.LOGIN ? 'login' : 'register'
		naverSSOButton.href = `${storefrontUrl}/on/demandware.store/Sites-KR-Site/ko_KR/Login-InitNaverSSO?mode=${mode}`
		const naverSSOImage = document.createElement('img')
		naverSSOImage.src =
			context === ContextViews.LOGIN
				? `${uaOkta.staticRootUrl}img/NaverSignIn.png`
				: `${uaOkta.staticRootUrl}img/NaverSignUp.png`
		naverSSOButton.append(naverSSOImage)
		naverSSODiv.append(naverSSOButton)
		const form = document.querySelector<HTMLFormElement>(`div[data-form-context="${context}"] form`)
		form?.appendChild(naverSSODiv)
	}

	#removeNaverSSO(): void {
		const naverSSODiv = document.querySelector<HTMLAnchorElement>('.naver-sso')
		naverSSODiv?.remove()
	}

	#handleError(error: Error): void {
		// Logs errors that occur when configuring the widget.
		// Remove or replace this with your own custom error handler.
		logger.error(error.message, error)
	}

	#logError(message: string, obj?: object) {
		logger.error(message, {
			application: this.#application?.clientId,
			brand: this.#brand?.name,
			locale: this.#locale,
			version: uaOkta.version,
			...obj,
		})
	}

	#logInfo(message: string, obj?: object) {
		logger.info(message, {
			application: this.#application?.clientId,
			brand: this.#brand?.name,
			locale: this.#locale,
			version: uaOkta.version,
			...obj,
		})
	}

	#removeFooter(): void {
		document.querySelector('div[class="footer-element"]')?.remove()
	}

	#triggerFlow({ context }: { context?: ContextViews }): void {
		if (context === ContextViews.LOGIN && this.#flow === Flows.REGISTER) {
			document.querySelectorAll<HTMLElement>('[data-se="enroll"]')?.[0]?.click()
		} else if (
			!!context &&
			[ContextViews.LOGIN, ContextViews.CHALLENGE_AUTHENTICATOR].includes(context) &&
			this.#flow === Flows.RESET_PASSWORD
		) {
			document.querySelectorAll<HTMLElement>('[data-se="forgot-password"]')?.[0]?.click()
		}
	}

	/**
	 * When the user changes views within the widget ie Login -> Register -> Login
	 * There's a lag in when the `afterRender` event triggers and when the DOM actually updates/
	 * Because of this, the following will work on initial render, but will require supporting styles
	 * re: ~/scss/common.scss
	 */
	#hideFormElements({ context }: { context: ContextViews }): void {
		const elements = this.#brand.hideFormElements?.[context] ?? []

		elements.forEach((element) => {
			// Get Actual Element
			const el = document.querySelector(`[data-se="${element}"]`)
			this.#loginContainerDiv?.setAttribute(`data-no-show-${element}`, 'true')
			// Now find Div Container and hide element
			const elementDiv = el?.closest('div')

			if (elementDiv) {
				elementDiv.classList.add('hidden')
			} else {
				logger.warn(`hideFormElements: Could not find element div for ${element}`)
			}
		})
	}

	render(): void {
		this.#widget.on('afterRender', (eventContext: EventContext) => this.#handleAfterRender(eventContext))
		this.#widget.on('afterError', (context: EventContext, error: EventErrorContext) => {
			this.#logError('afterError', { context, error })
		})
		this.#widget.on('ready', (eventContext: EventContext) => this.#handleReady(eventContext))

		this.#widget.renderEl({ el: this.#element }, OktaUtil?.completeLogin, this.#handleError)
	}
}
